import { Plus } from "lucide-react";
import { useState } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQueries,
  useLibraryAssetsQuery,
  type LibraryAsset,
} from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  storedDomainSummary,
  type BackgroundDomainSummary,
} from "@/lib/backgroundDomains";
import {
  storedProfileSummary,
  type BackgroundProfileSummary,
} from "@/lib/backgroundProfile";
import { formatDate } from "@/lib/format";
import { libraryAssetHref } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { AssetStateBadges } from "@/ui/library/AssetStateBadges";
import { CatalogGroup } from "@/ui/library/CatalogGroup";
import { CatalogDetails, CatalogRow } from "@/ui/library/CatalogRow";
import { LibrarySectionHeader } from "@/ui/library/LibrarySectionHeader";
import { EmptyState, LoadingState, StaleDataNotice } from "@/ui/states";

import { NamedReadFailureState } from "./NamedReadFailure";
import { NewBackgroundDomainDialog } from "./NewBackgroundDomainDialog";
import { NewBackgroundProfileDialog } from "./NewBackgroundProfileDialog";

/**
 * The canonical BG-sets catalog: Set files, then Domains (#859).
 *
 * ## WHY THIS REPLACES THE GENERIC CATALOG FOR THIS ONE SLUG
 *
 * `LibraryCatalog` lists `background_set` as an undifferentiated table of typed
 * assets. The reference (`/library/bg-sets`) shows two named groups with
 * different meanings and different creation actions: `SET FILES` over the
 * shared prompt/extraction documents every domain relies on at runtime, and
 * `DOMAINS` over the independently versioned intake sets a project pins one by
 * one. #908 published `CatalogGroup` for exactly this shape and named this
 * ticket as the leaf that fills it.
 *
 * IT ALSO SHOWS WHAT A DOMAIN IS, which the generic catalog structurally
 * cannot: catalog reads carry no content by contract, and a domain's clinical
 * identity — its runtime id, its interview position, how many prompts it asks —
 * lives only in the stored version. So each row's exact head version is read
 * (`useAssetVersionQueries`, the same hook the pinned-questionnaire surface
 * uses) and the facts are shown where the reference shows them. A read that has
 * not answered leaves those facts OFF the row rather than guessing them, and a
 * read that failed says so once for the group.
 *
 * ## SET FILES LISTS THE INTERVIEW PROFILES, AND SAYS SO WHEN THERE ARE NONE
 *
 * The reference lists `bg_conversation`, `bg_extraction` and `preamble_config`
 * there. Those documents are real — they are in the pack, at
 * `conversation_agent/{lang}/bg_sets/` — but they are not library assets in
 * their own right: the seed manifest deliberately imports the seven DOMAIN
 * files alone, because "pinning one clinical set must never claim (and conflict
 * on) those common files". The S7 contract folds them into a
 * `background_profile` family whose parts are "one profile's named parts, not
 * arbitrary files or an aggregate BG set".
 *
 * #858 LANDED THAT FAMILY, and its own `librarySections` entry maps it to THIS
 * rail section — "the Lovable reference groups Set files and Domains on ONE
 * `/library/bg-sets` screen". Since this catalog replaces the generic one for
 * that slug, a profile would be reachable only by typing its URL unless this
 * group lists it. So it does, through the same catalog read, and every row
 * links to the page `LibraryAssetRoute` already routes.
 *
 * #914 SUPPLIES THE CREATION CONTROL AND THE EDITOR this group was waiting for.
 * Until then there was neither: the generic dialog authors free parts while
 * this family's tone is typed, so a control whose submit cannot succeed would
 * have been the dead affordance ADR 0001 forbids. `NewBackgroundProfileDialog`
 * is that destination — a name, the variant, and the languages the profile
 * carries — and `BackgroundProfilePage` is where its three named parts are
 * opened. An empty group still says what the service actually holds rather than
 * showing the pack's three files as rows, which would be the fabricated seed
 * rows §2 of the contract rules out in the same sentence that asks for truthful
 * state.
 */

/** The section's own one-sentence explanation of what Set files holds. */
const SET_FILES_DESCRIPTION =
  "Shared prompt and extraction files that every BG domain relies on at runtime.";

/**
 * Why Set files has no rows, in the service's terms rather than as an absence.
 *
 * The same discipline `LibraryCatalog`'s governed-inventory notice follows: a
 * group a clinician expected content in is owed a sentence, not a blank.
 */
const SET_FILES_EMPTY =
  "No interview profile has been authored yet, so every project inherits the base clinical pack's own shared prompt and extraction files unchanged. Create one to give a project its own opening, tone and transitions.";

const DOMAINS_DESCRIPTION =
  "Each domain is an independently versioned set of intake prompts. Open a domain to edit them.";

export function BackgroundCatalog({
  sectionLabel,
  sectionDescription,
}: {
  sectionLabel: string;
  /** The section's one sentence, declared beside its rail entry (#908). */
  sectionDescription: string;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<LibraryAsset | null>(null);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [copyingProfile, setCopyingProfile] = useState<LibraryAsset | null>(
    null,
  );

  const catalog = useLibraryAssetsQuery(showArchived);
  const retryCatalog = useRetryPausedQuery(
    queryKeys.libraryAssets(showArchived),
  );
  const write = useCapability("library.write");

  // THE ROWS THIS SURFACE MAY PRESENT, or `null` when it holds none it can
  // stand behind. Derived ONCE for both groups, because "may we list what we
  // have?" is one question about one read and two copies of the answer is how
  // two groups on one screen come to disagree.
  //
  // `null` covers two states that are NOT "the library is empty":
  //
  //   * no response yet — a first load in flight, or a first load that failed,
  //     where `data` is simply absent;
  //   * a TERMINALLY refused refresh. Query-core keeps the previous `data`
  //     across a failed refetch, so a 401/403 would otherwise go on presenting
  //     clinical content the service has just said this caller may not see —
  //     the rule `BgSetAdoptionPicker` states for the same catalog.
  //
  // A NON-terminal failed refresh keeps its rows deliberately: those are real
  // rows of a knowable age, and `StaleDataNotice` below says how old.
  const readable =
    catalog.data !== undefined &&
    !(catalog.isError && isTerminalQueryError(catalog.error))
      ? catalog.data.items
      : null;
  const domains = (readable ?? []).filter(
    (asset) => asset.assetType === "background_set",
  );
  // #858's shared interview profile shares this rail section, so it is listed
  // here or it is not listed anywhere.
  const profiles = (readable ?? []).filter(
    (asset) => asset.assetType === "background_profile",
  );
  // The exact head of every listed domain. `useAssetVersionQueries` shares
  // `queryKeys.assetVersion` with the domain page, so opening a row afterwards
  // reads what this already fetched rather than issuing the request twice.
  const heads = useAssetVersionQueries(
    // BOTH GROUPS' HEADS in one call, because both rows carry facts that live
    // only in the stored version: a domain's runtime id and interview position,
    // and a profile's language coverage and session-introduction posture. One
    // hook keeps `failedAssetIds` a single fact about one read rather than two
    // notices about the same outage.
    [...profiles, ...domains].map((asset) => ({
      assetId: asset.id,
      version: asset.latestVersion,
    })),
  );

  const createProfileControl = (
    <Tooltip
      content={
        write.reason ?? "Create a new shared interview profile in the library"
      }
    >
      <Button
        variant="outline"
        size="sm"
        disabled={write.reason !== null}
        onClick={() => setCreatingProfile(true)}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New interview profile
      </Button>
    </Tooltip>
  );

  const createControl = (
    <Tooltip
      content={write.reason ?? "Create a new background domain in the library"}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={write.reason !== null}
        onClick={() => setCreating(true)}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New domain
      </Button>
    </Tooltip>
  );

  return (
    <>
      {/* #908's shared section header. The archive toggle is the SECTION's —
          it changes which rows both groups would list — while the creation
          control belongs to the Domains group, which is where the reference
          puts it. */}
      <LibrarySectionHeader
        sectionLabel={sectionLabel}
        description={sectionDescription}
        actions={
          // The house toggle form: the label stays constant, so `aria-pressed`
          // reads as a toggle rather than as a second button, and one state
          // drives the query, the retry key, the pressed state and the variant.
          <Button
            variant={showArchived ? "primary" : "outline"}
            size="sm"
            aria-pressed={showArchived}
            onClick={() => setShowArchived((value) => !value)}
          >
            Show archived
          </Button>
        }
      />

      <div className="space-y-8">
        <CatalogGroup
          title="Set files"
          description={SET_FILES_DESCRIPTION}
          actions={createProfileControl}
        >
          {/* The SAME read the Domains group renders from, so the two groups
              cannot disagree about what the library holds — and no second
              request for a fact one response already carries. A failed or
              pending catalog is reported once, by the Domains group below,
              because one read failing twice is one failure.

              WHICH IS WHY THIS SAYS NOTHING UNTIL THE READ HAS ANSWERED.
              `profiles` is derived from a list that is empty both when the
              library holds no profile and when this surface has not been told
              what the library holds — and only the first of those is
              "no interview profile has been authored yet". Claiming it over an
              unread catalog would be the surface asserting a fact it does not
              have, while the state that explains the absence renders further
              down the page. */}
          {readable === null ? null : profiles.length === 0 ? (
            <Notice>{SET_FILES_EMPTY}</Notice>
          ) : (
            <Card className="@container overflow-hidden">
              <ul aria-label={`${sectionLabel} interview profiles`}>
                {profiles.map((asset) => {
                  const detail = heads.byAssetId.get(asset.id);
                  return (
                    <li
                      key={asset.id}
                      className="border-b border-border last:border-b-0"
                    >
                      <ProfileRow
                        asset={asset}
                        summary={
                          detail ? storedProfileSummary(detail.content) : null
                        }
                        copyRefusal={write.reason}
                        onCopy={() => setCopyingProfile(asset)}
                      />
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </CatalogGroup>

        <CatalogGroup
          title="Domains"
          description={DOMAINS_DESCRIPTION}
          actions={createControl}
        >
          {catalog.data ? (
            // A HELD REFRESH OVER CACHED ROWS IS STILL A FAILED REFRESH:
            // query-core keeps `status: "success"` with the previous rows while
            // `fetchStatus` is `"paused"`, so `isError` alone would let the list
            // present rows of unknown age as current.
            <StaleDataNotice
              updatedAt={catalog.dataUpdatedAt}
              onRetry={retryCatalog}
              visible={
                catalog.isPaused ||
                (catalog.isError && !isTerminalQueryError(catalog.error))
              }
            />
          ) : null}

          {catalog.isPaused && catalog.data === undefined ? (
            // The held read, which `isPending` cannot express: it stays
            // `"pending"` while nothing is in flight.
            <NamedReadFailureState
              failure={catalog.failureReason}
              isPaused
              retry={retryCatalog}
              subject="BG domains"
              title="BG domains could not be loaded"
              description="The Editor Service did not return the shared library's background domains."
              deniedDescription="You are not authorized to read the shared clinical library."
              offlineDescription="The shared library's background domains could not be reached. They load on their own when the connection returns."
            />
          ) : catalog.isPending ? (
            <LoadingState label={`Loading ${sectionLabel} assets`} rows={4} />
          ) : catalog.isError &&
            (!catalog.data || isTerminalQueryError(catalog.error)) ? (
            <QueryErrorState
              error={catalog.error}
              subject="BG domains"
              title="BG domains could not be loaded"
              description="The Editor Service did not return the shared library's background domains."
              deniedDescription="You are not authorized to read the shared clinical library."
              onRetry={() => void catalog.refetch()}
            />
          ) : domains.length === 0 ? (
            <EmptyState
              title="No background domains yet"
              description={
                showArchived
                  ? "No background domain exists in the library."
                  : "No active background domain is in this section. Archived domains stay hidden until shown."
              }
              action={createControl}
            />
          ) : (
            <>
              {heads.failedAssetIds.size > 0 ? (
                // ONE NOTICE FOR THE GROUP, not one per row: the rows below are
                // still complete and correct as CATALOG rows — every identity,
                // version, state and adoption count is the catalog's own — and
                // what is missing is the clinical detail those reads carry.
                <Notice
                  action={
                    <Button variant="outline" onClick={heads.retryFailed}>
                      Try again
                    </Button>
                  }
                >
                  {heads.failedAssetIds.size} of these domains could not have
                  their stored content read, so their prompt counts and runtime
                  identities are not shown. Every row below is otherwise
                  complete.
                </Notice>
              ) : null}

              <Card className="@container overflow-hidden">
                <ul aria-label={`${sectionLabel} domains`}>
                  {domains.map((asset) => {
                    const detail = heads.byAssetId.get(asset.id);
                    const summary = detail
                      ? storedDomainSummary(detail.content)
                      : null;
                    return (
                      <li
                        key={asset.id}
                        className="border-b border-border last:border-b-0"
                      >
                        <DomainRow
                          asset={asset}
                          summary={summary}
                          {...(asset.capabilities.seeded
                            ? { onCopy: () => setCopying(asset) }
                            : {})}
                        />
                      </li>
                    );
                  })}
                </ul>
              </Card>
            </>
          )}
        </CatalogGroup>
      </div>

      {creating ? (
        <NewBackgroundDomainDialog onClose={() => setCreating(false)} />
      ) : null}
      {copying ? (
        <NewBackgroundDomainDialog
          copyFrom={copying}
          onClose={() => setCopying(null)}
        />
      ) : null}
      {creatingProfile ? (
        <NewBackgroundProfileDialog onClose={() => setCreatingProfile(false)} />
      ) : null}
      {copyingProfile ? (
        <NewBackgroundProfileDialog
          copyFrom={copyingProfile}
          onClose={() => setCopyingProfile(null)}
        />
      ) : null}
    </>
  );
}

/**
 * One domain, compact, in #908's shared row.
 *
 * The reference's own metadata order — `… · N steps · <provenance> · used by N
 * projects` — with the state badges beside the name where a clinician scans for
 * them. The runtime identity goes under `Details` rather than on the line: it is
 * the technical evidence the bounded scope keeps available and out of normal
 * navigation, and it is what a clinician needs when a project's roster names a
 * domain they cannot find by display name.
 *
 * ONE DIVERGENCE IN THAT LINE, and it is the reference's mock rather than a
 * layout choice: it reads `Updated Jun 4 by Maya`, one fact joining a date to a
 * person. This service publishes `createdBy` — "the principal that created the
 * asset" — and `updatedAt`, "last head change", which moves on any publish,
 * archive or restore by anyone. There is no updater field, so the joined
 * sentence would attribute a change to somebody the service never named. The
 * two are stated separately, as `LibraryCatalog`'s row already states them.
 */
function DomainRow({
  asset,
  summary,
  onCopy,
}: {
  asset: LibraryAsset;
  /** The head version's own reading, or `null` when it is not in hand. */
  summary: BackgroundDomainSummary | null;
  onCopy?: () => void;
}) {
  const adoption = asset.capabilities.adoptionCount;

  return (
    <CatalogRow
      to={libraryAssetHref(asset.assetType, asset.id)}
      name={asset.name}
      titleAdornment={<AssetStateBadges capabilities={asset.capabilities} />}
      meta={[
        <span key="version" className="font-mono text-xs">
          {asset.latestVersion}
        </span>,
        summary === null ? null : (
          <span key="prompts">
            {summary.itemCount} {summary.itemCount === 1 ? "prompt" : "prompts"}
          </span>
        ),
        summary?.order === null || summary === null ? null : (
          <span key="order">Position {summary.order}</span>
        ),
        // TWO FACTS, NOT ONE SENTENCE. `createdBy` is "the principal that
        // created the asset" and `updatedAt` is "last head change" — a publish,
        // an archive or a restore, by whoever performed it. Joining them reads
        // as "X made this change", which the contract does not say and the
        // service cannot: there is no updater field. `LibraryCatalog`'s own row
        // states the same pair separately, and its comment gives the same
        // reason; this row had merged them.
        <span key="author" className="min-w-0 truncate">
          Created by {asset.createdBy}
        </span>,
        <span key="updated">Updated {formatDate(asset.updatedAt)}</span>,
        <span key="adoption">
          Used by {adoption} {adoption === 1 ? "project" : "projects"}
        </span>,
      ]}
      actions={
        onCopy ? (
          // `text-xs`, the scale the rest of the row uses — the same measurement
          // `PathwayCatalog`'s member row records for the same control.
          <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={onCopy}
            >
              Create editable copy
            </Button>
          </Tooltip>
        ) : undefined
      }
      details={
        // WRAPPED BY THE CALLER, which is `CatalogRow`'s contract: `details`
        // goes straight into `DataRow`'s `columns` slot, and `CatalogDetails`
        // is the pair of flex items — the end-of-row chevron and the
        // full-basis panel beneath — that slot is shaped for. Passing the
        // panel bare puts the evidence permanently open and squeezes the name
        // column to nothing, which is what it did before this wrapper.
        <CatalogDetails subject={asset.name}>
          <dl className="grid gap-0.5 font-mono text-[11px] text-muted-foreground">
            <div className="break-all">
              {/* THE RUNTIME IDENTITY, read off the stored document rather than
                  inferred from the asset name or the coordinate — the two can
                  legitimately differ, and the document's own `domain` is what
                  the engine reads back. */}
              domain: {summary?.domain || "not reported by this version"}
            </div>
            <div className="break-all">
              placement:{" "}
              {asset.placementCoordinate ??
                "owned by the base pack manifest, not by a coordinate"}
            </div>
            <div>adoption_count: {adoption}</div>
          </dl>
        </CatalogDetails>
      }
    />
  );
}

/**
 * One shared interview profile, compact (#858's family, listed by #859).
 *
 * The domain row's facts, with this family's own in place of the ones a profile
 * does not have: no prompt count and no interview position — those belong to a
 * DOMAIN document — and instead the two facts that decide whether a project can
 * adopt it and what it will do. Nothing is invented to fill the line: an absent
 * fact is an absent entry, which is `CatalogMeta`'s own rule, and every one of
 * these is read off the exact head version rather than guessed from the name.
 *
 * WHY THE LANGUAGES ARE ON THE LINE. A project may only adopt a profile
 * carrying a complete variant at every language it serves — the engine's own
 * `_validate_bg_variant`, projected by `required_profile_languages` — so this is
 * the fact that decides whether this row is usable at all, and reading it after
 * a refusal is too late.
 *
 * `Create editable copy` has a destination since #914 and is offered for every
 * profile, not only seeded ones: a profile's variant coordinate is immutable, so
 * copying is how a second interview flavour is started from an existing one.
 */
function ProfileRow({
  asset,
  summary,
  copyRefusal,
  onCopy,
}: {
  asset: LibraryAsset;
  /** The head version's own reading, or `null` when it is not in hand. */
  summary: BackgroundProfileSummary | null;
  /** Why a copy cannot be started, or `null`. */
  copyRefusal: string | null;
  onCopy: () => void;
}) {
  const adoption = asset.capabilities.adoptionCount;

  return (
    <CatalogRow
      to={libraryAssetHref(asset.assetType, asset.id)}
      name={asset.name}
      titleAdornment={<AssetStateBadges capabilities={asset.capabilities} />}
      meta={[
        <span key="version" className="font-mono text-xs">
          {asset.latestVersion}
        </span>,
        summary === null || summary.completeLanguages.length === 0 ? null : (
          <span key="languages">{summary.completeLanguages.join(", ")}</span>
        ),
        summary === null || summary.sessionIntroEnabled === null ? null : (
          <span key="intro">
            {summary.sessionIntroEnabled
              ? "Separate opening turn"
              : "No separate opening turn"}
          </span>
        ),
        <span key="author" className="min-w-0 truncate">
          Created by {asset.createdBy}
        </span>,
        <span key="updated">Updated {formatDate(asset.updatedAt)}</span>,
        <span key="adoption">
          Used by {adoption} {adoption === 1 ? "project" : "projects"}
        </span>,
      ]}
      actions={
        // GATED, for the reason the group's own create control is: a copy
        // creates an asset, and a reader pressing it would reach a dialog whose
        // only outcome is a refused POST.
        <Tooltip
          content={
            copyRefusal ??
            (asset.capabilities.seeded
              ? "Part of the base clinical library. The original stays as it is — make an editable copy to create your own version."
              : "Start a new interview profile at another variant from this one's content.")
          }
        >
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            disabled={copyRefusal !== null}
            onClick={onCopy}
          >
            Create editable copy
          </Button>
        </Tooltip>
      }
      details={
        <CatalogDetails subject={asset.name}>
          <dl className="grid gap-0.5 font-mono text-[11px] text-muted-foreground">
            <div className="break-all">
              placement:{" "}
              {asset.placementCoordinate ??
                "owned by the base pack manifest, not by a coordinate"}
            </div>
            <div>
              banned_phrases:{" "}
              {summary?.bannedPhraseCount ?? "not reported by this version"}
            </div>
            <div>adoption_count: {adoption}</div>
          </dl>
        </CatalogDetails>
      }
    />
  );
}
